/**
 * Clearing the art this device captured off the trainer's own screen.
 *
 * There are TWO stores, written by two different scanners and holding the same
 * kind of thing — a crop lifted from a phone screenshot:
 *
 *   · localSpriteStore — box / detail / catch OCR crops, keyed by the record
 *   · dexCropCapture   — the Pokédex grid scan's per-cell crops
 *
 * Neither is displayed anywhere any more (2026-08-13): the art comes from the
 * official mirror on every surface, and the dex scan no longer captures at all,
 * because doing it cost a full-frame decode and a PNG encode per cell inside
 * the window a frame has to finish in. What is left is bytes on the device with
 * nothing reading them.
 *
 * So this is a real privacy-and-space control, and it is deliberately one
 * action across both stores. A trainer asked to clear "stored sprites" does not
 * know there are two, and a control that empties one of them is the kind of
 * half-truth that made the old "Uploaded screenshots" button worth deleting.
 */
import { clearAllStoredSprites, listStoredSprites, storedSpriteBytes } from "./localSpriteStore";
import { clearAllDexCrops, dexCropStoreStats } from "./dexCropCapture";

export interface StoredSpriteUsage {
  /** Images held across both stores. */
  count: number;
  /** Roughly how much space they take. Base64 in localStorage, so approximate. */
  bytes: number;
}

export function storedSpriteUsage(): StoredSpriteUsage {
  try {
    const crops = dexCropStoreStats();
    return {
      count: listStoredSprites().length + crops.count,
      bytes: storedSpriteBytes() + crops.bytes,
    };
  } catch {
    // A blocked or unreadable localStorage (iOS private mode) is "nothing
    // stored" as far as a trainer is concerned, not an error worth a dialog.
    return { count: 0, bytes: 0 };
  }
}

/** Empty both stores. Returns how many images actually went. */
export function clearStoredSprites(): number {
  let cleared = 0;
  try {
    cleared += clearAllStoredSprites();
  } catch {
    /* keep going — clearing one store must not be blocked by the other */
  }
  try {
    cleared += clearAllDexCrops();
  } catch {
    /* as above */
  }
  return cleared;
}

/** "12.4 MB" / "840 KB" / "0 KB" — a size a person would say. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
